/**
 * 知识（`#knowledge`）：店铺知识条目只读展示（10-16：政策属于知识，不进指南；方案基准 2.1）。
 * 一份条目两个入口：推送给 N2 ／ N3，knowledge-search 工具按问题检索。
 * 推送口径（标签体系 v2 批 4，画像 pv5 起）：准入（店铺）→ 订单状态（系统事实）→ 逐条判「这条和这件事对不对得上」
 * （建事项时 Jev 判、按事项缓存）＋ 意图标签加分；意图不再是卡口。旧画像按意图含诉求标签推。
 * 类型（标签体系 v2 批 5）：条目 kind 是标签字典「内容分类 › 知识类型」的取值（业务包 knowledge.json · kinds，模板预置
 * 政策、商品、数据表、话术，客户可加）；按类型看、筛。类型只用于管理、筛选、统计，推送、检索、执行前把关都不读它。
 * 改内容改业务包 knowledge.json，走 PR（新增条目时在 kind 里选一个类型）。
 */
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { type PlanKnowledgeEntry, planErrorMessage, usePlanKnowledge } from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import {
	LAYER_ALL,
	LAYER_FALLBACK,
	LayerBadge,
	LayerDetail,
	layerOptions,
	matchLayer,
} from "../kit/layer";
import { PageJump } from "../kit/page-jump";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { STATE_LABEL } from "../recall/recall-labels";

const stateName = (s: string) => STATE_LABEL[s] ?? s;
const ALL = "__all__";
const NONE = "__none__";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap text-sm text-text-primary">{children}</div>
		</div>
	);
}

export function KnowledgeTab() {
	const q = usePlanKnowledge();
	const [open, setOpen] = useState<PlanKnowledgeEntry | null>(null);
	const [kind, setKind] = useState(ALL);
	const [layer, setLayer] = useState(LAYER_ALL);
	const layers = q.data?.layers?.length ? q.data.layers : LAYER_FALLBACK;
	const kinds = q.data?.kinds ?? [];
	const kindOf = (e: PlanKnowledgeEntry) => kinds.find((k) => k.key === e.kind);
	const kindName = (e: PlanKnowledgeEntry) => (e.kind ? (kindOf(e)?.name ?? e.kind) : "未分类");
	const intents = q.data?.intents ?? {};
	const intentName = (k: string) => intents[k] ?? k;
	const relevance = q.data?.push_mode === "relevance";
	const tagsOf = (e: PlanKnowledgeEntry) => e.intent_tags ?? e.intents ?? [];
	const stores = q.data?.stores ?? {};
	const storeText = (e: PlanKnowledgeEntry) =>
		e.stores?.length ? e.stores.map((s) => stores[s] ?? s).join("、") : "全部店";

	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !q.data)
		return (
			<Card>
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-knowledge-fail"
					/>
				</CardContent>
			</Card>
		);

	const cols: PlanColumn<PlanKnowledgeEntry>[] = [
		{
			key: "id",
			title: "编号",
			className: "w-48",
			render: (e) => <span className="font-mono">{e.id}</span>,
		},
		{ key: "title", title: "标题", className: "max-w-[180px]", render: (e) => e.title },
		{
			key: "kind",
			title: "类型",
			className: "w-20",
			render: (e) =>
				e.kind ? kindName(e) : <span className="text-text-muted">{kindName(e)}</span>,
		},
		{
			key: "layer",
			title: "层",
			className: "w-24",
			render: (e) => <LayerBadge row={e} layers={layers} />,
		},
		{
			key: "content",
			title: "内容",
			className: "max-w-[340px]",
			render: (e) => (
				<span className="block truncate text-text-secondary" title={e.content}>
					{e.content}
				</span>
			),
		},
		{
			key: "intents",
			title: relevance ? "意图标签（加分）" : "适用意图",
			render: (e) => <CellTags items={tagsOf(e).map(intentName)} />,
		},
		{ key: "stores", title: "适用店铺", className: "w-36", render: (e) => storeText(e) },
		{
			key: "constraint",
			title: "约束",
			className: "w-36",
			render: (e) =>
				e.constraint?.tools?.length ? (
					<span title={`执行前拦：${e.constraint.tools.join("、")}`}>
						<Badge tone="warning">拦 {e.constraint.tools.length} 个写工具</Badge>
					</span>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
		{
			key: "states",
			title: "订单状态条件",
			render: (e) =>
				e.data_states?.length ? (
					<CellTags items={e.data_states.map(stateName)} />
				) : (
					<span className="text-text-muted">不限</span>
				),
		},
	];

	const all = q.data.entries;
	const rows = all.filter(
		(e) =>
			(kind === ALL || (kind === NONE ? !e.kind : e.kind === kind)) && matchLayer(layer, e, layers),
	);
	const count = (k: string) => all.filter((e) => e.kind === k).length;
	const unclassified = all.filter((e) => !e.kind).length;
	const kindOptions = [
		{ value: ALL, label: `全部（${all.length}）` },
		...kinds.map((k) => ({ value: k.key, label: `${k.name}（${count(k.key)}）` })),
		...(unclassified ? [{ value: NONE, label: `未分类（${unclassified}）` }] : []),
	];
	return (
		<Card data-testid="plan-knowledge">
			<CardContent className="space-y-3 py-4">
				<div className="flex flex-wrap items-center gap-3">
					<span data-testid="plan-knowledge-kind-filter">
						<LabeledSelect label="类型" value={kind} onChange={setKind} options={kindOptions} />
					</span>
					<span data-testid="plan-knowledge-layer-filter">
						<LabeledSelect
							label="层"
							value={layer}
							onChange={setLayer}
							options={layerOptions(layers, all)}
						/>
					</span>
					<span
						className="min-w-0 flex-1 text-xs text-text-muted"
						data-testid="plan-knowledge-push-mode"
					>
						{relevance
							? "推送：只看客户所属店铺的条目 → 限订单状态的要相关订单状态对上 → 逐条判和这件事对不对得上（建事项时判一次、按事项缓存），意图标签只加分。约束类条目执行前照旧把关。类型不参与推送。"
							: "推送：条目的意图含事项的诉求标签，且订单状态对上才推。类型不参与推送。"}
						进来线、加分见 <PageJump to="recall" />
						；正文里的 {"{参数}"} 按条目所在店铺取，值见 <PageJump to="business-params" />
					</span>
				</div>
				{rows.length ? (
					<PlanTable
						columns={cols}
						rows={rows}
						rowKey={(e) => e.id}
						onRowClick={setOpen}
						density="compact"
						rowTestId="plan-knowledge-row"
					/>
				) : all.length ? (
					<EmptyState title="这个类型下没有条目" className="py-8" />
				) : (
					<EmptyState title="还没有知识条目" desc="在业务包 knowledge.json 里加条目。" />
				)}
				<div className="text-xs text-text-muted" data-testid="plan-knowledge-count">
					共 {rows.length} 条
				</div>
			</CardContent>
			<Drawer
				open={!!open}
				onClose={() => setOpen(null)}
				width="min(720px,95vw)"
				testId="plan-knowledge-drawer"
				title={
					<span>
						<span className="font-mono">{open?.id}</span> {open?.title}
					</span>
				}
			>
				{open ? (
					<>
						<DrawerSection title="内容">
							<div className="whitespace-pre-wrap text-sm text-text-primary">{open.content}</div>
						</DrawerSection>
						<DrawerSection title="层" testId="plan-knowledge-layer">
							<LayerDetail row={open} layers={layers} />
						</DrawerSection>
						<DrawerSection title="类型">
							<Field label="内容分类 › 知识类型（只用于管理、筛选、统计）">
								<span data-testid="plan-knowledge-kind">{kindName(open)}</span>
								{kindOf(open)?.desc ? (
									<span className="ml-2 text-xs text-text-muted">{kindOf(open)?.desc}</span>
								) : null}
							</Field>
						</DrawerSection>
						{open.constraint?.tools?.length ? (
							<DrawerSection title="约束（执行前把关）" testId="plan-knowledge-constraint">
								<Field label="拦哪些写工具">
									<span className="font-mono text-xs">{open.constraint.tools.join("、")}</span>
								</Field>
								<Field label="在什么订单状态下拦（没写取上面的订单状态条件）">
									{(open.constraint.data_states ?? open.data_states ?? []).length
										? (open.constraint.data_states ?? open.data_states ?? [])
												.map(stateName)
												.join("、")
										: "不限"}
								</Field>
								<Field label="例外（满足就不拦）">
									{Object.entries(open.constraint.unless ?? {}).length
										? Object.entries(open.constraint.unless ?? {})
												.map(([slot, v]) =>
													Array.isArray(v)
														? `${slot} 是 ${v.join("、")}`
														: `${slot} 属于「${v.class}」类`,
												)
												.join("；")
										: "无"}
									<span className="ml-2 text-xs text-text-muted">
										约束类条目怎么推给 Agent 见 <PageJump to="recall" />
									</span>
								</Field>
							</DrawerSection>
						) : null}
						<DrawerSection title="适用条件与依据">
							<Field
								label={
									relevance
										? "意图标签（只加分：诉求意图在里面时相关度加分，不在也照样判相关）"
										: "适用意图（诉求标签命中才用）"
								}
							>
								{tagsOf(open).map(intentName).join("、") || "—"}
								<span className="ml-2 text-xs text-text-muted">
									说明见 <PageJump to="tags" />
								</span>
							</Field>
							<Field label="订单状态条件（系统事实：有值时，事项相关订单处于其一才推送）">
								{open.data_states?.length ? open.data_states.map(stateName).join("、") : "不限"}
							</Field>
							<Field label="适用店铺">{storeText(open)}</Field>
							<Field label="检索关键词（没有 Jev 时用）">
								{(open.keywords ?? []).join("、") || "—"}
							</Field>
							<Field label="来源">{open.source || "—"}</Field>
						</DrawerSection>
					</>
				) : null}
			</Drawer>
		</Card>
	);
}
